import React, { useState } from 'react';
import { Lock, Mail, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { authService } from '../shared/services/AuthService';
import { NoorPieceLogo } from './NoorPieceLogo';
import { LuxuryModal } from './ui/LuxuryModal';

interface AuthLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  notice?: string | null;
  initialMode?: 'signin' | 'signup';
}

export const AuthLoginModal: React.FC<AuthLoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  notice,
  initialMode = 'signin',
}) => {
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Keep mode in sync with props when re-opened
  React.useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setErrorMessage('');
    }
  }, [isOpen, initialMode]);

  const handleSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    setTimeout(() => {
      if (import.meta.env.DEV) {
        console.log('[AuthGuard] User successfully signed in:', email);
      }
      authService.login(email);
      setIsLoading(false);
      onSuccess();
    }, 400);
  };

  const handleSignUp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setErrorMessage('Please enter an email address.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    setTimeout(() => {
      if (import.meta.env.DEV) {
        console.log('[AuthGuard] User successfully registered new account:', email);
      }
      authService.login(email, firstName || 'Patron', lastName || 'Member');
      setIsLoading(false);
      onSuccess();
    }, 400);
  };

  return (
    <LuxuryModal
      isOpen={isOpen}
      onClose={onClose}
      subtitle="Atelier Sanctuary"
      icon={<Lock className="w-3.5 h-3.5" />}
      ariaLabel="Atelier Sanctuary Authentication"
      maxWidthClass="max-w-md sm:max-w-lg"
    >
      <div className="space-y-6">
        {/* Header Branding */}
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-2xl bg-[#F3ECE2] border border-[#D8C29B]/80 shadow-xs">
              <NoorPieceLogo className="w-8 h-8 text-[#8C6239]" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-serif text-[#3C2D22] font-normal">
            Atelier Sanctuary Access
          </h2>
          <p className="text-xs text-[#6A5948] font-serif italic">
            Authentication Required for Reserved Atelier Services
          </p>

          {/* Action Protected Notice Banner */}
          {notice && (
            <div className="mt-3 p-3.5 rounded-xl bg-[#F3ECE2] border border-[#D8C29B] text-[#3C2D22] text-xs text-left flex items-start space-x-2.5 shadow-xs">
              <Lock className="w-4 h-4 text-[#8C6239] shrink-0 mt-0.5" />
              <span className="leading-relaxed font-sans">{notice}</span>
            </div>
          )}
        </div>

        {/* Mode Tabs */}
        <div className="flex rounded-2xl bg-[#F3ECE2] p-1.5 border border-[#D8C29B]/80">
          <button
            type="button"
            onClick={() => {
              setMode('signin');
              setErrorMessage('');
            }}
            className={`flex-1 py-2.5 rounded-xl text-xs font-sans font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
              mode === 'signin'
                ? 'bg-[#8C6239] text-white shadow-sm'
                : 'text-[#6A5948] hover:text-[#3C2D22]'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('signup');
              setErrorMessage('');
            }}
            className={`flex-1 py-2.5 rounded-xl text-xs font-sans font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
              mode === 'signup'
                ? 'bg-[#8C6239] text-white shadow-sm'
                : 'text-[#6A5948] hover:text-[#3C2D22]'
            }`}
          >
            Create Account
          </button>
        </div>

        {/* Error Notification */}
        {errorMessage && (
          <div className="p-3 rounded-xl bg-rose-100/80 border border-rose-300 text-rose-800 text-xs text-center font-sans font-medium">
            {errorMessage}
          </div>
        )}

        {/* Form Body */}
        {mode === 'signin' ? (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label className="block text-[11px] uppercase font-sans tracking-widest text-[#B5895A] font-semibold mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B5895A]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="patron@noorpieces.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-[#D8C29B] text-[#3C2D22] text-sm placeholder-[#6A5948]/50 focus:outline-none focus:ring-2 focus:ring-[#B5895A]/40 focus:border-[#B5895A] transition-all shadow-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase font-sans tracking-widest text-[#B5895A] font-semibold mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B5895A]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-[#D8C29B] text-[#3C2D22] text-sm placeholder-[#6A5948]/50 focus:outline-none focus:ring-2 focus:ring-[#B5895A]/40 focus:border-[#B5895A] transition-all shadow-xs"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#8C6239] to-[#76512F] text-white font-sans text-xs uppercase tracking-[0.18em] font-bold hover:bg-[#76512F] transition-all shadow-md flex items-center justify-center space-x-2 cursor-pointer disabled:opacity-50 mt-2 hover:scale-[1.005] active:scale-[0.995]"
            >
              {isLoading ? (
                <span>Authenticating...</span>
              ) : (
                <>
                  <span>Sign In & Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSignUp} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase font-sans tracking-widest text-[#B5895A] font-semibold mb-1.5">
                  First Name
                </label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Eleanor"
                  className="w-full px-3.5 py-3 rounded-xl bg-white border border-[#D8C29B] text-[#3C2D22] text-sm placeholder-[#6A5948]/50 focus:outline-none focus:ring-2 focus:ring-[#B5895A]/40 focus:border-[#B5895A] transition-all shadow-xs"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase font-sans tracking-widest text-[#B5895A] font-semibold mb-1.5">
                  Last Name
                </label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Vance"
                  className="w-full px-3.5 py-3 rounded-xl bg-white border border-[#D8C29B] text-[#3C2D22] text-sm placeholder-[#6A5948]/50 focus:outline-none focus:ring-2 focus:ring-[#B5895A]/40 focus:border-[#B5895A] transition-all shadow-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase font-sans tracking-widest text-[#B5895A] font-semibold mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B5895A]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="patron@noorpieces.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-[#D8C29B] text-[#3C2D22] text-sm placeholder-[#6A5948]/50 focus:outline-none focus:ring-2 focus:ring-[#B5895A]/40 focus:border-[#B5895A] transition-all shadow-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase font-sans tracking-widest text-[#B5895A] font-semibold mb-1.5">
                Create Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B5895A]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-[#D8C29B] text-[#3C2D22] text-sm placeholder-[#6A5948]/50 focus:outline-none focus:ring-2 focus:ring-[#B5895A]/40 focus:border-[#B5895A] transition-all shadow-xs"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#8C6239] to-[#76512F] text-white font-sans text-xs uppercase tracking-[0.18em] font-bold hover:bg-[#76512F] transition-all shadow-md flex items-center justify-center space-x-2 cursor-pointer disabled:opacity-50 mt-2 hover:scale-[1.005] active:scale-[0.995]"
            >
              {isLoading ? (
                <span>Creating Account...</span>
              ) : (
                <>
                  <span>Create Account & Continue</span>
                  <Sparkles className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        )}

        {/* Cancel Option */}
        <div className="text-center pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[#6A5948] hover:text-[#3C2D22] underline underline-offset-4 cursor-pointer transition-colors font-sans"
          >
            Cancel and continue browsing as guest
          </button>
        </div>
      </div>
    </LuxuryModal>
  );
};
